import HoverPrefetchLink from '@/components/hover-prefetch-link';
import { websiteEnv } from '@/constants/website-env';
import useIsMobile from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '../ui/popover';
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const ColorsFamilyItem = ({
  subColorOptions,
  generateSubColorLink,
  generateSelectAllLink,
  urlColorRewrites,
  onTogglePopover,
  popoverVisible,
  onPopoverOpenChange,
  swatchData,
  colorsFamilyLabel,
}: {
  subColorOptions: {
    code: string;
    option_id: string;
    rewrite: string;
    label?: string;
    swatch_data: {
      value: string;
      thumbnail?: string;
    };
  }[];
  urlColorRewrites: string[];
  generateSubColorLink: (param: { label: string; rewrite: string; value: string; prevIsActive: boolean }) => string;
  generateSelectAllLink: (
    prevIsSelectAll: boolean,
    subColors: {
      label: string;
      rewrite: string;
      value: string;
    }[],
  ) => string;
  onTogglePopover: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  popoverVisible: boolean;
  onPopoverOpenChange: (nextOpen: boolean) => void;
  colorsFamilyLabel: string;
  swatchData: null | {
    value?: string;
    thumbnail?: string;
  };
}) => {
  const isMobile = useIsMobile();
  const curColorsFamilySubColorRewrites: string[] = subColorOptions.map(item => item.rewrite);
  const curColorsFamilySubColorRewriteMap = curColorsFamilySubColorRewrites.reduce<Record<string, true>>((acc, cur) => {
    acc[cur] = true;

    return acc;
  }, {});
  const colorsFamilyRewrite = colorsFamilyLabel.toLowerCase().replace(/\s/g, '-');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const colorAllTriggerRef = useRef<HTMLDivElement | null>(null);
  const { curColorFamilyHasSelected, curColorFamilySelectedCount, allSelectedColorRewriteMap } = (() => {
    const allSelectedColorRewriteMap: Record<string, true> = {};
    let curColorFamilyHasSelected = false;
    let curColorFamilySelectedCount = 0;

    for (const urlColorRewrite of urlColorRewrites) {
      allSelectedColorRewriteMap[urlColorRewrite] = true;

      if (curColorsFamilySubColorRewriteMap[urlColorRewrite]) {
        curColorFamilyHasSelected = true;
        curColorFamilySelectedCount++;
      }
    }

    return {
      curColorFamilyHasSelected,
      curColorFamilySelectedCount: Math.min(curColorFamilySelectedCount, subColorOptions.length),
      allSelectedColorRewriteMap,
    };
  })();

  const isSelectedAll =
    curColorFamilyHasSelected && curColorFamilySelectedCount === curColorsFamilySubColorRewrites.length;
  const colorRectStyle = createColorStyle(swatchData?.thumbnail, swatchData?.value);

  const nextSelectAllLink = generateSelectAllLink(
    isSelectedAll,
    subColorOptions.map(x => ({
      label: x.label || '',
      value: x.option_id,
      rewrite: x.rewrite,
    })),
  );
  const isColorAll = colorsFamilyRewrite === 'all';

  const handlePopoverTriggerClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isMobile) {
      onTogglePopover(e);
    }

    setDropdownVisible(true);
  };

  const triggerNode = (
    <div
      className='flex flex-none flex-col items-center'
      ref={colorAllTriggerRef}
      onClick={handlePopoverTriggerClick}
    >
      <div
        className={cn(
          'cursor-pointer rounded-[7px] border-1 border-solid border-80583F p-0.5 lg_rounded-[10px] lg_p-[5px] ',
          curColorFamilyHasSelected ? 'border-80583F' : 'border-transparent',
        )}
      >
        {isColorAll || isMobile ? (
          <div
            style={isColorAll ? undefined : colorRectStyle}
            className={cn(
              ' block h-10 w-10 rounded-[5px] bg-contain lg_h-[66px] lg_w-[66px]',
              isWhite(swatchData?.value || '') ? 'border-1 border-solid border-E7DED5' : '',
            )}
          >
            {isColorAll ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='67'
                height='66'
                fill='none'
                viewBox='0 0 67 66'
                className='h-auto w-full'
              >
                <g clipPath='url(#clip0_13530_35876)'>
                  <path
                    fill='#EFEFE6'
                    d='M.5 0h22v22H.5z'
                  />
                  <path
                    fill='#E0D1BD'
                    d='M22.5 0h22v22h-22z'
                  />
                  <path
                    fill='#D68B59'
                    d='M44.5 0h22v22h-22z'
                  />
                  <path
                    fill='#F0D3C9'
                    d='M.5 22h22v22H.5z'
                  />
                  <path
                    fill='#CC8986'
                    d='M22.5 22h22v22h-22z'
                  />
                  <path
                    fill='#9A1834'
                    d='M44.5 22h22v22h-22z'
                  />
                  <path
                    fill='#C4C3D0'
                    d='M.5 44h22v22H.5z'
                  />
                  <path
                    fill='#6F6E71'
                    d='M22.5 44h22v22h-22z'
                  />
                  <path
                    fill='#002712'
                    d='M44.5 44h22v22h-22z'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_13530_35876'>
                    <rect
                      width='66'
                      height='66'
                      x='0.5'
                      fill='#fff'
                      rx='5'
                    />
                  </clipPath>
                </defs>
              </svg>
            ) : null}
          </div>
        ) : (
          <HoverPrefetchLink
            style={colorRectStyle}
            href={nextSelectAllLink}
            prefetch={isMobile}
            className={cn(
              ' block h-10 w-10 rounded-[5px] bg-contain lg_h-[66px] lg_w-[66px]',
              isWhite(swatchData?.value || '') ? 'border-1 border-solid border-E7DED5' : '',
            )}
            aria-label={colorsFamilyLabel}
          />
        )}
      </div>
      <div
        className={clsx(
          ' mt-0.5 text-center text-[13px] leading-[18px]  lg_mt-[5px] lg_text-[15px] lg_leading-5',
          'cursor-pointer',
          curColorFamilyHasSelected ? 'text-80583F underline underline-offset-4' : '',
        )}
      >
        {colorsFamilyLabel}
        {curColorFamilySelectedCount > 0 ? `(${curColorFamilySelectedCount})` : null}
      </div>
    </div>
  );

  const dropdownNode = (
    <div
      className={clsx(
        'max-h-[294px] lg_max-h-[320px]',
        'list-none gap-x-[10px] gap-y-6 overflow-y-auto overscroll-none ',
        'px-1.5 py-4 lg_pl-2.5 lg_pr-3',
        'theme-scroll-bar',
      )}
    >
      {!isColorAll && (
        <div className='mb-[15px] pl-5 lg_pl-2.5'>
          <HoverPrefetchLink
            href={nextSelectAllLink}
            onClick={() => {
              setDropdownVisible(false);
              onPopoverOpenChange(false);
            }}
            className='flex items-center gap-3'
            prefetch={isMobile}
          >
            <span
              className={clsx(
                'h-5 w-5',
                'border-1 border-solid border-80583F',
                isSelectedAll ? 'bg-80583F' : 'bg-transparent',
                'flex items-center justify-center',
              )}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='15'
                height='13'
                fill='none'
                viewBox='0 0 15 13'
                className={clsx(!isSelectedAll && 'hidden')}
              >
                <path
                  stroke='#fff'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='1.5'
                  d='m13.5 1.305-8.4 10-3.6-3.75'
                />
              </svg>
            </span>
            <span className='text-sm leading-5 text-#403732'>SELECT ALL</span>
          </HoverPrefetchLink>
        </div>
      )}

      <div
        className={clsx(
          'flex flex-wrap',
          subColorOptions.length > 5 && 'lg_grid lg_grid-cols-5',
          ' max-w-[calc(100vw_-_34px)] lg_max-w-none',
          'gap-y-2.5',
          isColorAll ? 'gap-x-[9px]' : 'gap-x-2.5',
        )}
      >
        {subColorOptions.map((colorOption: any, index: number) => {
          if (!colorOption) {
            return null;
          }

          const colorRewrite = colorOption.rewrite;
          const swatch_data = colorOption.swatch_data;
          const isSelected = isSelectedAll || allSelectedColorRewriteMap[colorRewrite];
          const nextSubColorLink = generateSubColorLink({
            prevIsActive: isSelected,
            label: colorOption.label,
            value: colorOption.option_id,
            rewrite: colorRewrite,
          });
          const colorRectStyle = createSubColorStyle(swatch_data?.thumbnail, swatch_data?.value);

          return (
            <HoverPrefetchLink
              href={nextSubColorLink}
              key={index}
              onClick={() => {
                setDropdownVisible(false);
                onPopoverOpenChange(false);
              }}
              className='group flex cursor-pointer flex-col items-center justify-start'
              prefetch={isMobile}
            >
              <div
                className={cn(
                  ' h-10 w-10 rounded-[50%] border-2 border-solid p-[3px] lg_group-hover_border-80583F ',
                  isSelected ? 'border-80583F' : 'border-transparent',
                )}
              >
                <div
                  style={colorRectStyle}
                  className={cn(
                    'h-full rounded-[50%] bg-contain ',
                    isWhite(swatch_data?.value || '') ? 'border-1 border-E7DED5' : '',
                  )}
                />
              </div>

              <span
                className={clsx(
                  ' text-center text-[13px] leading-5 text-#403732 lg_group-hover_text-80583F ',
                  'w-[60px]',
                )}
              >
                {websiteEnv.isUnitedKingdom && colorOption?.label === 'Multi Color'
                  ? 'Multicolour'
                  : colorOption?.label}
              </span>
            </HoverPrefetchLink>
          );
        })}
      </div>
    </div>
  );

  return isMobile ? (
    <Popover
      open={popoverVisible}
      onOpenChange={onPopoverOpenChange}
    >
      <PopoverAnchor asChild>{triggerNode}</PopoverAnchor>
      <PopoverContent
        className='z-[120] rounded-[10px] bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.1)]'
        side='bottom'
        sideOffset={20}
        collisionPadding={{
          left: 10,
          right: 10,
        }}
      >
        {dropdownNode}
        <PopoverArrow className='h-auto w-4 fill-white' />
      </PopoverContent>
    </Popover>
  ) : (
    <TooltipProvider delayDuration={150}>
      <Tooltip
        onOpenChange={setDropdownVisible}
        open={dropdownVisible}
      >
        <TooltipTrigger
          asChild
          onClick={e => {
            if (isColorAll) {
              e.preventDefault();
            }
          }}
        >
          {triggerNode}
        </TooltipTrigger>
        <TooltipContent
          side='bottom'
          className='rounded-[10px] bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.1)]'
          onPointerDownOutside={e => {
            const isClickColorAll = !!colorAllTriggerRef.current?.contains(e.target as HTMLElement);

            if (isClickColorAll && isColorAll) {
              e.preventDefault();
            }
          }}
        >
          <TooltipArrow className='h-auto w-4 fill-white' />
          {dropdownNode}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const isWhite = (value: string) => {
  const lowercaseValue = value.toLowerCase();

  return lowercaseValue === '#fff' || lowercaseValue === '#ffffff';
};

const createColorStyle = (thumbnail: string | undefined, value: string | undefined) => {
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    return {
      backgroundImage: `url("${thumbnail}")`,
    };
  }

  return {
    backgroundColor: value,
  };
};

const createSubColorStyle = (thumbnail: string | undefined, value: string | undefined) => {
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    return {
      backgroundImage: `url("${process.env.NEXT_PUBLIC_BASE_PATH}/media/attribute/swatch/swatch_thumb/110x90${thumbnail}")`,
    };
  }

  return {
    backgroundColor: value,
  };
};

export default ColorsFamilyItem;
